import * as React from "react";
import { useState, useRef } from "react";
import {
  Text,
  PrimaryButton,
  DefaultButton,
  IconButton,
  TooltipHost,
  Callout,
  DirectionalHint,
  Stack,
  Icon,
  mergeStyles,
  getTheme,
} from "@fluentui/react";
import { HomeRegular } from "@fluentui/react-icons";
import { AuthUser } from "../../auth/authDialog";
import { Entitlement } from "./types";

const theme = getTheme();

const headerStyle = mergeStyles({
  backgroundColor: theme.palette.themePrimary,
  color: theme.palette.white,
  padding: "4px 44px 4px 12px", // right padding reserves space for Excel's native info button
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexShrink: 0,
});

const logoStyle = mergeStyles({
  display: "flex",
  alignItems: "center",
  gap: "6px",
});

const headerIconButtonStyles = {
  root: { color: theme.palette.white, height: 28, width: 28 },
  rootHovered: { color: theme.palette.white, backgroundColor: theme.palette.themeDarkAlt },
  rootPressed: { color: theme.palette.white, backgroundColor: theme.palette.themeDark },
  icon: { fontSize: 14 },
};

const calloutBodyStyle = mergeStyles({
  padding: "12px 16px",
  minWidth: 220,
});

const buildInfoStyle = mergeStyles({
  borderTop: `1px solid ${theme.palette.neutralLight}`,
  marginTop: 8,
  paddingTop: 8,
});

export interface HeaderProps {
  user: AuthUser | null;
  entitlement: Entitlement | null;
  authBusy: boolean;
  refreshing: boolean;
  onSignIn: () => void;
  onSignOut: () => void;
  onRefresh: () => void;
}

const Header: React.FunctionComponent<HeaderProps> = ({
  user,
  entitlement,
  authBusy,
  refreshing,
  onSignIn,
  onSignOut,
  onRefresh,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const userButtonRef = useRef<HTMLDivElement>(null);

  const subscriptionOk = entitlement != null && ["active", "trialing"].includes(entitlement.status);

  return (
    <>
      <div className={headerStyle}>
        <div className={logoStyle}>
          <HomeRegular style={{ fontSize: 18, color: theme.palette.white }} />
          <Text variant="medium" styles={{ root: { color: theme.palette.white, fontWeight: 600 } }}>
            SheetyBanks
          </Text>
        </div>

        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 2 }}>
          {user && (
            <TooltipHost content="Force all SHEETYBANKS functions in this workbook to recalculate">
              <IconButton
                iconProps={{ iconName: "Refresh" }}
                onClick={onRefresh}
                disabled={refreshing}
                ariaLabel="Refresh data"
                styles={headerIconButtonStyles}
              />
            </TooltipHost>
          )}
          {user ? (
            <div ref={userButtonRef}>
              <TooltipHost content="Account">
                <IconButton
                  iconProps={{ iconName: "Contact" }}
                  onClick={() => setMenuOpen(!menuOpen)}
                  ariaLabel="Account menu"
                  styles={headerIconButtonStyles}
                />
              </TooltipHost>
            </div>
          ) : (
            <PrimaryButton
              text={authBusy ? "Opening…" : "Sign in"}
              onClick={onSignIn}
              disabled={authBusy}
              styles={{
                root: {
                  minWidth: 0,
                  height: 26,
                  backgroundColor: theme.palette.white,
                  color: theme.palette.themePrimary,
                },
                rootHovered: { backgroundColor: theme.palette.neutralLighter, color: theme.palette.themeDark },
                label: { fontWeight: 600 },
              }}
            />
          )}
        </Stack>
      </div>

      {/* Rendered outside the header div: the Callout's Layer leaves a placeholder
          span in the DOM, which would become a flex item and shift the icons. */}
      {menuOpen && user && (
        <Callout
          target={userButtonRef.current}
          onDismiss={() => setMenuOpen(false)}
          directionalHint={DirectionalHint.bottomRightEdge}
          isBeakVisible={false}
          gapSpace={4}
        >
          <div className={calloutBodyStyle}>
            <Stack tokens={{ childrenGap: 8 }}>
              <Stack tokens={{ childrenGap: 2 }}>
                <Text styles={{ root: { fontWeight: 600 } }}>{user.name || user.email || "Signed in"}</Text>
                {user.name && user.email && (
                  <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    {user.email}
                  </Text>
                )}
              </Stack>

              {entitlement && (
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
                  <Icon
                    iconName={subscriptionOk ? "SkypeCircleCheck" : "Warning"}
                    styles={{
                      root: {
                        color: subscriptionOk ? theme.palette.green : theme.palette.orangeLighter,
                        fontSize: 14,
                      },
                    }}
                  />
                  <Text variant="small">
                    {subscriptionOk
                      ? `Plan: ${entitlement.tier ?? "subscriber"} · ${entitlement.status}`
                      : `No active subscription (${entitlement.status})`}
                  </Text>
                </Stack>
              )}

              <DefaultButton
                text="Sign out"
                onClick={() => {
                  setMenuOpen(false);
                  onSignOut();
                }}
                styles={{ root: { minWidth: 0, height: 28 } }}
              />

              <div className={buildInfoStyle}>
                <Text variant="xSmall" styles={{ root: { color: theme.palette.neutralTertiary } }}>
                  SheetyBanks v{__ADDIN_VERSION__} ({__BUILD_MODE__}) · built{" "}
                  {new Date(__BUILD_TIME__).toLocaleDateString()}
                </Text>
              </div>
            </Stack>
          </div>
        </Callout>
      )}
    </>
  );
};

export default Header;
